import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Robot, ArrowsClockwise, Lightning } from "@phosphor-icons/react";
import { PageHeader } from "./_shared";

/**
 * Phase 10.2D — Batch AI Quality Monitor dashboard.
 * Aggregates recent WhatsApp lead analyses. Read-only, never messages customers.
 */
export default function WhatsAppQaDashboard() {
  const [latest, setLatest] = useState(null);
  const [running, setRunning] = useState(false);
  const [period, setPeriod] = useState("last_7d");
  const [maxLeads, setMaxLeads] = useState(20);

  const load = () => {
    api.get("/admin/wa-qa/batch/latest")
      .then(r => setLatest(r.data || null))
      .catch(err => console.error("qa batch fetch failed", err));
  };
  useEffect(load, []);

  const runBatch = () => {
    setRunning(true);
    api.post("/admin/wa-qa/batch", { period, max_leads: maxLeads })
      .then(r => {
        setLatest(r.data);
        toast.success(`Batch analysis complete — ${r.data?.leads_analyzed_ok || 0} leads`);
      })
      .catch(err => toast.error(err?.response?.data?.detail || "Batch failed"))
      .finally(() => setRunning(false));
  };

  const agg = latest?.aggregate || {};

  return (
    <div data-testid="wa-qa-dashboard">
      <PageHeader
        title="WhatsApp QA Monitor"
        subtitle={latest ? `Last run: ${latest.generated_at} · ${latest.leads_analyzed_ok}/${latest.leads_considered} leads` : "No batch runs yet — trigger one below."}
        right={
          <div className="flex items-center gap-2">
            <select
              value={period}
              onChange={e => setPeriod(e.target.value)}
              data-testid="wa-qa-batch-period"
              className="bg-[#0D0D0D] border border-[#2A2A2A] text-[10px] py-2 px-2 uppercase tracking-widest text-white/80"
            >
              <option value="last_24h">Last 24h</option>
              <option value="last_7d">Last 7d</option>
            </select>
            <select
              value={maxLeads}
              onChange={e => setMaxLeads(Number(e.target.value))}
              data-testid="wa-qa-batch-maxleads"
              className="bg-[#0D0D0D] border border-[#2A2A2A] text-[10px] py-2 px-2 uppercase tracking-widest text-white/80"
            >
              {[10, 20, 30, 50, 80].map(n => <option key={n} value={n}>Max {n}</option>)}
            </select>
            <button
              type="button"
              onClick={runBatch}
              disabled={running}
              data-testid="wa-qa-batch-run"
              className="text-[10px] tracking-widest uppercase font-bold py-2 px-3 border border-amber-500 text-amber-300 hover:bg-amber-500/10 disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              <ArrowsClockwise size={12} weight="bold" />
              {running ? "Running…" : "Run Batch Analysis"}
            </button>
          </div>
        }
      />

      {!latest && (
        <div data-testid="wa-qa-dashboard-empty" className="border border-[#2A2A2A] bg-[#0D0D0D]/60 p-6 text-center text-[11px] text-white/50">
          <Robot size={20} weight="bold" className="inline mb-2 text-amber-400" />
          <div>No batch analysis stored yet. Choose a period and click <span className="text-amber-300">Run Batch Analysis</span>.</div>
        </div>
      )}

      {latest && (
        <div className="space-y-5">
          {/* --- KPI strip --- */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="wa-qa-kpis">
            <Kpi label="Analyzed"   value={agg.total_conversations_analyzed} />
            <Kpi label="Completed"  value={agg.completed_intakes}       tone="good" />
            <Kpi label="Stuck"      value={agg.stuck_conversations}     tone="danger" />
            <Kpi label="Needs Review" value={agg.needs_admin_review}    tone="warn" />
            <Kpi label="Sold/NA"    value={agg.sold_or_not_available}   tone="warn" />
            <Kpi label="Call Requests"    value={agg.call_requests}          tone="warn" />
            <Kpi label="Price Requests"   value={agg.price_or_offer_requests} tone="warn" />
            <Kpi label="No Response · Link"  value={agg.no_response_after_form_link} />
            <Kpi label="No Response · SoftF/U" value={agg.no_response_after_soft_followup} />
          </div>

          {/* --- Panels --- */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Panel title="Top drop-off steps" testid="wa-qa-dropoff-panel">
              {(agg.top_drop_off_steps || []).length === 0
                ? <Empty />
                : agg.top_drop_off_steps.map(r => (
                    <div key={r.step} className="flex justify-between text-[11px] text-white/80 py-1 border-b border-[#1a1a1a]/70">
                      <span>{r.step}</span>
                      <span className="text-white/50">{r.count}</span>
                    </div>
                  ))}
            </Panel>

            <Panel title="Common customer confusions" testid="wa-qa-confusions">
              {(agg.common_customer_confusions || []).length === 0
                ? <Empty />
                : <ul className="list-disc list-inside space-y-1 text-[11px] text-white/80">
                    {agg.common_customer_confusions.map((c, i) => <li key={i}>{c}</li>)}
                  </ul>}
            </Panel>

            <Panel title="Automation failure patterns" testid="wa-qa-failures">
              {(agg.automation_failure_patterns || []).length === 0
                ? <Empty />
                : <ul className="list-disc list-inside space-y-1 text-[11px] text-white/80">
                    {agg.automation_failure_patterns.map((c, i) => <li key={i}>{c}</li>)}
                  </ul>}
            </Panel>

            <Panel title="Wording suggestions (Ops review only)" testid="wa-qa-wording-suggestions">
              {(agg.recommended_wording_changes || []).length === 0
                ? <Empty />
                : <ul className="list-disc list-inside space-y-1 text-[11px] text-white/80">
                    {agg.recommended_wording_changes.map((c, i) => <li key={i} className="italic">"{c}"</li>)}
                  </ul>}
            </Panel>
          </div>

          {(agg.sample_lead_ids_for_review || []).length > 0 && (
            <Panel title="Sample leads to review" testid="wa-qa-samples">
              <div className="flex flex-wrap gap-1.5">
                {agg.sample_lead_ids_for_review.map(id => (
                  <a key={id} href={`/admin/crm/${id}`} data-testid={`wa-qa-sample-${id}`}
                     className="text-[10px] px-2 py-0.5 border border-[#2A2A2A] text-white/70 hover:bg-white/5 rounded-sm">
                    {id.slice(0, 8)}…
                  </a>
                ))}
              </div>
            </Panel>
          )}

          <div className="text-[9px] text-white/30 uppercase tracking-widest">
            <Lightning size={10} weight="bold" className="inline mr-1" />
            Internal QA only · No customer-facing messages · Wording changes require separate approval
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, tone = "neutral" }) {
  const tones = {
    good:    "border-emerald-500/60 text-emerald-300",
    warn:    "border-amber-500/60 text-amber-300",
    danger:  "border-red-500/60 text-red-300",
    neutral: "border-[#2A2A2A] text-white/80",
  };
  return (
    <div className={`border ${tones[tone] || tones.neutral} bg-[#0D0D0D]/60 p-2.5`}>
      <div className="text-[9px] uppercase tracking-widest text-white/40">{label}</div>
      <div className="text-lg font-bold">{Number.isFinite(value) ? value : 0}</div>
    </div>
  );
}

function Panel({ title, children, testid }) {
  return (
    <div data-testid={testid} className="border border-[#2A2A2A] bg-[#0D0D0D]/60 p-3">
      <div className="text-[9px] uppercase tracking-widest text-white/40 mb-2">{title}</div>
      {children}
    </div>
  );
}
function Empty() { return <div className="text-[11px] text-white/40 italic">(none reported)</div>; }
